import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  subscribeMusic, getStation, getRadioStation, getRadioError,
  playRadioStation, getRadioFavorites, toggleRadioFavorite,
} from "@/lib/audio";
import "./LiveRadioPicker.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Live Radio": real AM/FM stations playing their live broadcast over the
// internet. Search by call letters, station name or state; ★ to save favorites.
export default function LiveRadioPicker({ compact = false }) {
  const [, setTick] = useState(0);
  const [tab, setTab] = useState("search"); // "search" | "favs"
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => subscribeMusic(() => setTick((t) => t + 1)), []);
  const isRadio = getStation() === "radio";

  const search = useCallback(async (q) => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/radio/search`, { params: { q: q || "" } });
      setResults((r.data && r.data.stations) || []);
      if (!((r.data && r.data.stations) || []).length) setError("No stations found — try call letters (WGN) or a state (Texas).");
    } catch (e) {
      setResults([]);
      setError(e.response
        ? ((e.response.data && e.response.data.detail) || "The radio directory didn't answer — try again.")
        : "Can't reach the game server — check your connection, reopen the game and try again.");
    }
    setLoading(false);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (isRadio && !loaded) search("");
  }, [isRadio, loaded, search]);

  if (!isRadio) return null;
  const now = getRadioStation();
  const favs = getRadioFavorites();
  const favIds = new Set(favs.map((f) => f.id));
  const list = tab === "favs" ? favs : results;

  return (
    <div className={`liveradio${compact ? " is-compact" : ""}`} data-testid="live-radio">
      <div className="liveradio-now" data-testid="live-radio-now">
        {now ? (
          <>
            <span className={`liveradio-band band-${(now.band || "web").toLowerCase()}`}>{now.band || "WEB"}</span>
            <span className="liveradio-now-name">{now.name}</span>
            <span className="liveradio-live">● LIVE</span>
          </>
        ) : (
          <span className="liveradio-hint">Pick a station below to start the radio</span>
        )}
      </div>
      {now && getRadioError() && (
        <div className="liveradio-error" data-testid="live-radio-offline">That station isn't answering right now — pick another.</div>
      )}
      <div className="liveradio-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "search"} className={`liveradio-tab${tab === "search" ? " is-active" : ""}`} onClick={() => setTab("search")} data-testid="live-radio-tab-search">Stations</button>
        <button type="button" role="tab" aria-selected={tab === "favs"} className={`liveradio-tab${tab === "favs" ? " is-active" : ""}`} onClick={() => setTab("favs")} data-testid="live-radio-tab-favs">★ Favorites ({favs.length})</button>
      </div>
      {tab === "search" && (
        <form
          className="liveradio-search"
          onSubmit={(e) => { e.preventDefault(); search(query); }}
        >
          <input
            type="search"
            className="liveradio-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Call letters, station or state"
            aria-label="Search radio stations"
            data-testid="live-radio-input"
          />
          <button type="submit" className="liveradio-go" data-testid="live-radio-search">Search</button>
        </form>
      )}
      {loading && tab === "search" && <div className="liveradio-msg">Tuning in…</div>}
      {!loading && error && tab === "search" && <div className="liveradio-msg">{error}</div>}
      {tab === "favs" && favs.length === 0 && <div className="liveradio-msg">Tap ☆ next to a station to save it here.</div>}
      <ul className="liveradio-list" data-testid="live-radio-list">
        {list.map((s) => (
          <li key={s.id} className={`liveradio-row${now && now.id === s.id ? " is-playing" : ""}`}>
            <button
              type="button"
              className="liveradio-pick"
              onClick={() => playRadioStation(s)}
              data-testid={`live-radio-station-${s.id}`}
            >
              <span className={`liveradio-band band-${(s.band || "web").toLowerCase()}`}>{s.band || "WEB"}</span>
              <span className="liveradio-meta">
                <span className="liveradio-name">{s.name}</span>
                {s.city && <span className="liveradio-city">{s.city}</span>}
              </span>
            </button>
            <button
              type="button"
              className={`liveradio-fav${favIds.has(s.id) ? " is-fav" : ""}`}
              onClick={() => toggleRadioFavorite(s)}
              aria-label={favIds.has(s.id) ? "Remove from favorites" : "Add to favorites"}
              data-testid={`live-radio-fav-${s.id}`}
            >
              {favIds.has(s.id) ? "★" : "☆"}
            </button>
          </li>
        ))}
      </ul>
      <div className="liveradio-note">Real stations, live over the internet. Uses your data plan on mobile.</div>
    </div>
  );
}
